import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Settings2, ShoppingBag } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { AbandonedCheckout } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { useAbandonedCheckoutColumns } from '../components/abandoned-checkout-columns';
import { abandonedCheckoutFilters } from '../components/abandoned-checkout-filters';
import { useAbandonedCheckouts } from '../hooks/use-abandoned-checkouts';
import { useRecoveryTemplateEnabled } from '../hooks/use-recovery-template-enabled';
import { useSendRecoveryAction } from '../hooks/use-send-recovery-action';

const ROUTE_ID = '/_authed/_active/s/$storeId/abandoned-checkouts/' as const;
const ROUTE_PATH = '/s/$storeId/abandoned-checkouts/' as const;

function AbandonedCheckouts() {
  const { t } = useTranslation('abandonedCheckouts');
  const store = useStore();
  const { can } = usePermissions();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { onSend, sendingId } = useSendRecoveryAction(store.id);

  const filters = useMemo(
    () => abandonedCheckoutFilters({ t, currency: store.defaultCurrency }),
    [store.defaultCurrency, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });

  const query = useAbandonedCheckouts(store.id, list.params);
  const templateEnabled = useRecoveryTemplateEnabled(store.id);
  const sendRow = useCallback((checkout: AbandonedCheckout) => onSend(checkout.id), [onSend]);

  const columns = useAbandonedCheckoutColumns({
    storeId: store.id,
    canWrite: can('orders.write'),
    templateEnabled,
    sendingId,
    onSend: sendRow,
  });
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'abandoned-checkouts',
  });

  const narrowed = Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={
          <Can permission="settings.read">
            <Button asChild variant="outline" data-tour="abandoned.list.settings">
              <Link to="/s/$storeId/settings/recovery" params={{ storeId: store.id }}>
                <Settings2 aria-hidden />
                {t('settings.open')}
              </Link>
            </Button>
          </Can>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={ShoppingBag}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="abandoned.list.empty"
          tourLink
        />
      ) : (
        <DataTable
          pillFilter="recovered"
          table={table}
          search={false}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/abandoned-checkouts/$checkoutId',
              params: { storeId: store.id, checkoutId: row.id },
            })
          }
          getRowLabel={(row) => row.email}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'abandoned.list.table' }}
        />
      )}
    </PageContent>
  );
}

export function AbandonedCheckoutsPage() {
  return (
    <FeatureErrorBoundary name="abandonedCheckouts.list">
      <AbandonedCheckouts />
    </FeatureErrorBoundary>
  );
}
